import React, { useRef } from 'react';
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';

const formatCurrency = (value) =>
    new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value || 0);

const fmtDate = (value) => {
    if (!value) return '—';
    const iso = typeof value === 'string' ? value : new Date(value).toISOString();
    const [y, m, d] = iso.substring(0, 10).split('-');
    return `${d}/${m}/${y}`;
};

/* estilos en línea para que la impresión conserve el formato */
const S = {
    sheet: { background: '#fff', color: '#111', padding: '24px 28px', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '12px', lineHeight: 1.4 },
    header: { display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: '4px 16px', paddingBottom: '10px', borderBottom: '1px solid #999' },
    label: { fontWeight: 700 },
    cols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '14px' },
    th: { textAlign: 'left', fontWeight: 700, borderBottom: '1px solid #ccc', padding: '4px 6px', fontSize: '11px' },
    thR: { textAlign: 'right', fontWeight: 700, borderBottom: '1px solid #ccc', padding: '4px 6px', fontSize: '11px' },
    td: { padding: '3px 6px' },
    tdR: { padding: '3px 6px', textAlign: 'right' },
    totalsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #999', fontWeight: 700 },
    pagar: { textAlign: 'right', marginTop: '10px', fontSize: '14px', fontWeight: 700 },
    firmas: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginTop: '48px', textAlign: 'center' },
    firmaLine: { borderTop: '1px solid #333', paddingTop: '4px' },
};

const Field = ({ label, value }) => (
    <div><span style={S.label}>{label}:</span> {value ?? '—'}</div>
);

const ColillaModal = ({ settlement, onClose }) => {
    const ref = useRef(null);
    const open = !!settlement;
    if (!open) return null;

    const emp = settlement.empleado || {};
    const lav = settlement.lavanderia || {};
    const val = settlement.valores || {};
    const horas = settlement.horas || {};
    const conceptos = settlement.conceptos || [];
    const devengados = conceptos.filter((c) => c.naturaleza === 'devengado');
    const deducciones = conceptos.filter((c) => c.naturaleza === 'deduccion');
    const totalDev = val.totalDevengados ?? 0;
    const totalDed = val.totalDeducciones ?? 0;
    const totalPagar = val.totalNeto ?? val.totalAPagar ?? 0;
    const horasLaboradas = horas.totalHorasAutorizadas || horas.totalHorasTrabajadas || 0;

    const handlePrint = () => {
        if (!ref.current) return;
        const w = window.open('', '_blank', 'width=950,height=680');
        if (!w) return;
        w.document.write(
            `<html><head><title>Colilla de Pago</title></head><body style="margin:0">${ref.current.outerHTML}</body></html>`
        );
        w.document.close();
        w.focus();
        setTimeout(() => { w.print(); }, 250);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Colilla de Pago</DialogTitle>
                </DialogHeader>

                <div ref={ref} style={S.sheet}>
                    {/* Encabezado */}
                    <div style={S.header}>
                        <Field label="Empleado" value={`${emp.cedula || ''}  ${emp.nombre || ''}`} />
                        <Field label="Jornada" value={settlement.configuracion?.jornada || '—'} />
                        <Field label="Banco" value={emp.banco} />
                        <Field label="Oficio" value={emp.oficio || emp.rol} />
                        <Field label="Horas laboradas" value={Number(horasLaboradas).toFixed(2)} />
                        <Field label="Cuenta" value={emp.cuenta} />
                        <Field label="Centro" value={lav.nombre} />
                        <Field label="Salario" value={formatCurrency(settlement.salario)} />
                        <Field
                            label="Fechas de pago"
                            value={`${fmtDate(settlement.periodo?.fechaInicio)} a ${fmtDate(settlement.periodo?.fechaFin)}`}
                        />
                    </div>

                    {/* Columnas devengado / deducción */}
                    <div style={S.cols}>
                        {/* Devengados */}
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={S.th}>Concepto devengado</th>
                                    <th style={S.thR}>Valor</th>
                                    <th style={S.thR}>Horas</th>
                                    <th style={S.thR}>Días</th>
                                </tr>
                            </thead>
                            <tbody>
                                {devengados.length === 0 ? (
                                    <tr><td style={S.td} colSpan={4}>—</td></tr>
                                ) : (
                                    devengados.map((c, i) => (
                                        <tr key={`d-${i}`}>
                                            <td style={S.td}>{c.codigo ? `${c.codigo} ` : ''}{c.nombre}</td>
                                            <td style={S.tdR}>+ {formatCurrency(c.montoCalculado)}</td>
                                            <td style={S.tdR}>{c.horas != null ? Number(c.horas).toFixed(2) : ''}</td>
                                            <td style={S.tdR}>{c.dias != null ? c.dias : ''}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                        {/* Deducciones */}
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={S.th}>Concepto deducción</th>
                                    <th style={S.thR}>Valor</th>
                                    <th style={S.thR}>Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deducciones.length === 0 ? (
                                    <tr><td style={S.td} colSpan={3}>—</td></tr>
                                ) : (
                                    deducciones.map((c, i) => (
                                        <tr key={`x-${i}`}>
                                            <td style={S.td}>{c.codigo ? `${c.codigo} ` : ''}{c.nombre}</td>
                                            <td style={S.tdR}>- {formatCurrency(c.montoCalculado)}</td>
                                            <td style={S.tdR}></td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Totales */}
                    <div style={S.totalsRow}>
                        <div>TOTAL DEVENGADOS {formatCurrency(totalDev)}</div>
                        <div>TOTAL DEDUCCIONES {formatCurrency(totalDed)}</div>
                    </div>
                    <div style={S.pagar}>TOTAL A PAGAR: {formatCurrency(totalPagar)}</div>

                    {/* Firmas */}
                    <div style={S.firmas}>
                        <div style={S.firmaLine}>Firma empleado</div>
                        <div style={S.firmaLine}>Firma autorizada</div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cerrar</Button>
                    <Button onClick={handlePrint}>
                        <Printer className="mr-2 h-4 w-4" /> Imprimir
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ColillaModal;
